import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  Box,
  Chip,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  BadgeRounded,
  RecordVoiceOverRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import ReportMetricCard from "components/reports/shared/ReportMetricCard";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import { getChartColors } from "theme";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";
import DashboardAllLinguists from "./DashboardAllLinguists";

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (value) =>
  new Intl.NumberFormat("en-US").format(Number(value || 0));

const KpiSkeleton = () => (
  <Grid container spacing={2} size={{ xs: 12 }}>
    {[0, 1].map((i) => (
      <Grid key={i} size={{ xs: 6, sm: 6 }}>
        <Skeleton variant="rounded" height={120} />
      </Grid>
    ))}
  </Grid>
);

// ─── main component ──────────────────────────────────────────────────────────

const DashboardV2AllLinguists = ({ user, dateSelection }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const chartColors = getChartColors(theme);
  const { refreshPage, handleErrorDialog } = useStateContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linguistsKPIs, setLinguistsKPIs] = useState(null);
  const DRAWER_INITIAL = { open: false, loading: false, title: "", subtitle: "", data: [], columns: [], summary: null, onRowClick: null };
  const [drillDown, setDrillDown] = useState(DRAWER_INITIAL);
  const closeDrillDown = () => setDrillDown(DRAWER_INITIAL);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get("/dashboard/all/linguists/linguistTotals")
      .then((res) => {
        setLinguistsKPIs(res?.data ?? null);
        setLoading(false);
      })
      .catch((err) => {
        const msg =
          err.response?.data?.message ||
          "Unable to load linguist summary. Please try again.";
        handleErrorDialog(msg, theme);
        setError(msg);
        setLoading(false);
      });
  }, [refreshPage, handleErrorDialog, theme]);

  // ── pie data ──────────────────────────────────────────────────────────────

  const byStatusPie = useMemo(
    () => [
      { id: 0, value: Number(linguistsKPIs?.byStatus?.Active || 0), label: "Active", color: chartColors.series[2] },
      { id: 1, value: Number(linguistsKPIs?.byStatus?.Inactive || 0), label: "Inactive", color: chartColors.series[4] },
      { id: 2, value: Number(linguistsKPIs?.byStatus?.["N/A"] || 0), label: "N/A", color: chartColors.series[5] },
    ],
    [linguistsKPIs, chartColors],
  );

  const byCertificationPie = useMemo(
    () => [
      { id: 0, value: Number(linguistsKPIs?.byCertification?.Certified || 0), label: "Certified", color: chartColors.series[0] },
      { id: 1, value: Number(linguistsKPIs?.byCertification?.["Non-certified"] || 0), label: "Non-certified", color: chartColors.series[3] },
    ],
    [linguistsKPIs, chartColors],
  );

  const byContractPie = useMemo(
    () => [
      { id: 0, value: Number(linguistsKPIs?.byContract?.Yes || 0), label: "Has Contract", color: chartColors.series[2] },
      { id: 1, value: Number(linguistsKPIs?.byContract?.No || 0), label: "No Contract", color: chartColors.series[4] },
    ],
    [linguistsKPIs, chartColors],
  );

  const byW9Pie = useMemo(
    () => [
      { id: 0, value: Number(linguistsKPIs?.byW9?.Yes || 0), label: "Has W9", color: chartColors.series[2] },
      { id: 1, value: Number(linguistsKPIs?.byW9?.No || 0), label: "No W9", color: chartColors.series[4] },
    ],
    [linguistsKPIs, chartColors],
  );

  const requirementCharts = useMemo(() => {
    const list = linguistsKPIs?.requirementCompliance || [];
    return list.map((item, idx) => ({
      key: item.key,
      sourceKey: item.sourceKey,
      title: `${item.name} Status`,
      data: [
        { id: 0, value: Number(item?.counts?.Yes || 0), label: "Yes", color: chartColors.series[2] },
        { id: 1, value: Number(item?.counts?.No || 0), label: "No", color: chartColors.series[4] },
        { id: 2, value: Number(item?.counts?.["N/A"] || 0), label: "N/A", color: chartColors.series[5] },
      ],
      index: idx,
    }));
  }, [linguistsKPIs, chartColors]);

  const LINGUIST_COLUMNS = [
    { field: "name", headerName: "Name", width: 200 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "email", headerName: "Email", width: 220 },
    { field: "phone", headerName: "Phone", width: 160 },
    { field: "certification", headerName: "Certification", width: 160 },
    { field: "contract", headerName: "Contract", width: 120 },
    { field: "w9", headerName: "W9", width: 100 },
    { field: "badge", headerName: "Badge", width: 110 },
    { field: "requirementsSummary", headerName: "Requirements", width: 260 },
  ];

  const LANGUAGE_COLUMNS = [
    { field: "language", headerName: "Language", width: 260 },
    { field: "count", headerName: "Linguists", width: 120 },
  ];

  const toLinguistRows = (list = []) => {
    return (list || []).map((item) => ({
      id: item.id,
      name: item.Name || "-",
      status: item.Status || "N/A",
      email: item.Email || "-",
      phone: item.PhoneNumber ? normalizePhoneNumber(item.PhoneNumber) : "-",
      certification: item.Certifications || "N/A",
      contract: item?.LinguistContractHistories?.[0]?.Status || "N/A",
      w9: item?.LinguistW9s?.[0]?.Status || "N/A",
      badge: item?.LinguistBadges?.[0]?.Status || "N/A",
      requirementsSummary: Object.entries(item?.RequirementStatuses || {})
        .map(([key, status]) => `${key}: ${status}`)
        .join(" | ") || "N/A",
    }));
  };

  const openLinguistProfileFromRow = (row) => {
    if (!row?.id) return;
    navigate(`/linguists/${row.id}`, { state: { ComingFrom: "/dashboard" } });
  };

  const fetchAndOpenLinguistDrillDown = (meta, params = {}) => {
    setDrillDown({ ...DRAWER_INITIAL, open: true, loading: true, onRowClick: openLinguistProfileFromRow, ...meta });
    api
      .get("/dashboard/all/linguists/linguists", { params })
      .then((res) => {
        const rows = toLinguistRows(res?.data || []);
        setDrillDown((prev) => ({ ...prev, loading: false, data: rows, columns: LINGUIST_COLUMNS }));
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.response?.data?.message || "Unable to load drill-down data.";
        handleErrorDialog(msg, theme);
        setDrillDown((prev) => ({ ...prev, loading: false }));
      });
  };

  const fetchAndOpenLanguagesDrillDown = () => {
    setDrillDown({
      ...DRAWER_INITIAL,
      open: true,
      loading: true,
      title: "Total Languages",
      subtitle: "Coverage across linguists",
      summary: { metrics: [{ label: "Total Languages", value: fmt(linguistsKPIs?.totalLanguages || 0), color: theme.palette.info.main }] },
      onRowClick: null,
    });

    api
      .get("/dashboard/all/linguists/linguists")
      .then((res) => {
        const all = res?.data || [];
        const counts = new Map();
        all.forEach((linguist) => {
          const names = new Set((linguist?.LinguistLanguages || [])
            .map((row) => row?.Language?.Name)
            .filter(Boolean));
          names.forEach((name) => counts.set(name, (counts.get(name) || 0) + 1));
        });

        const rows = Array.from(counts.entries())
          .map(([language, count]) => ({ language, count }))
          .sort((a, b) => b.count - a.count);

        setDrillDown((prev) => ({ ...prev, loading: false, data: rows, columns: LANGUAGE_COLUMNS }));
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.response?.data?.message || "Unable to load languages.";
        handleErrorDialog(msg, theme);
        setDrillDown((prev) => ({ ...prev, loading: false }));
      });
  };

  const mapSliceToFilterValue = (category, label) => {
    if (category === "Contract" || category === "W9" || category === "Badge") {
      if (label.includes("Has")) return "Yes";
      if (label.includes("No")) return "No";
      return "N/A";
    }
    return label;
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ pb: 3 }}>
      {/* V2 Analytics Summary */}
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Chip label="Linguist Analytics" size="small" color="primary" variant="outlined" />
          <Typography variant="body2" color="text.secondary">
            Linguist roster distribution and compliance summary
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* KPI cards */}
        {loading ? (
          <KpiSkeleton />
        ) : (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 6, sm: 6 }}>
              <ReportMetricCard
                title="Total Linguists"
                value={fmt(linguistsKPIs?.totalLinguists)}
                icon={<RecordVoiceOverRounded />}
                color="primary"
                tooltipTitle="Total linguists in the roster"
                onClick={() =>
                  fetchAndOpenLinguistDrillDown(
                    {
                      title: "Total Linguists",
                      subtitle: "All linguists",
                      summary: { metrics: [{ label: "Total", value: fmt(linguistsKPIs?.totalLinguists || 0), color: theme.palette.primary.main }] },
                    },
                    {},
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6 }}>
              <ReportMetricCard
                title="Total Languages"
                value={fmt(linguistsKPIs?.totalLanguages)}
                icon={<BadgeRounded />}
                color="info"
                tooltipTitle="Total languages covered by the linguist roster"
                onClick={fetchAndOpenLanguagesDrillDown}
              />
            </Grid>
          </Grid>
        )}

        {/* Distribution Pies */}
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Roster Distribution</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <ReportPieChart
              title="By Status"
              data={byStatusPie}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) =>
                fetchAndOpenLinguistDrillDown(
                  {
                    title: "By Status",
                    subtitle: `Selected: ${slice.label}`,
                    summary: { metrics: [{ label: "Count", value: fmt(slice.value), color: theme.palette.primary.main }] },
                  },
                  { category: "Status", value: mapSliceToFilterValue("Status", slice.label) },
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ReportPieChart
              title="By Certification"
              data={byCertificationPie}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) =>
                fetchAndOpenLinguistDrillDown(
                  {
                    title: "By Certification",
                    subtitle: `Selected: ${slice.label}`,
                    summary: { metrics: [{ label: "Count", value: fmt(slice.value), color: theme.palette.primary.main }] },
                  },
                  { category: "Certification", value: mapSliceToFilterValue("Certification", slice.label) },
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ReportPieChart
              title="Contract Status"
              data={byContractPie}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) =>
                fetchAndOpenLinguistDrillDown(
                  {
                    title: "Contract Status",
                    subtitle: `Selected: ${slice.label}`,
                    summary: { metrics: [{ label: "Count", value: fmt(slice.value), color: theme.palette.primary.main }] },
                  },
                  { category: "Contract", value: mapSliceToFilterValue("Contract", slice.label) },
                )
              }
            />
          </Grid>
        </Grid>

        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Compliance</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          {requirementCharts.length > 0 ? (
            requirementCharts.map((chart) => (
              <Grid key={chart.key || chart.index} size={{ xs: 12, sm: 6, md: 4 }}>
                <ReportPieChart
                  title={chart.title}
                  data={chart.data}
                  height={320}
                  legendPosition="bottom"
                  onSliceClick={(slice) =>
                    fetchAndOpenLinguistDrillDown(
                      {
                        title: chart.title,
                        subtitle: `Selected: ${slice.label}`,
                        summary: { metrics: [{ label: "Count", value: fmt(slice.value), color: theme.palette.primary.main }] },
                      },
                      {
                        category: "Requirement",
                        value: mapSliceToFilterValue("Requirement", slice.label),
                        requirementKey: chart.sourceKey || chart.key,
                      },
                    )
                  }
                />
              </Grid>
            ))
          ) : (
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReportPieChart
                title="W9 Status"
                data={byW9Pie}
                height={320}
                legendPosition="bottom"
                onSliceClick={(slice) =>
                  fetchAndOpenLinguistDrillDown(
                    {
                      title: "W9 Status",
                      subtitle: `Selected: ${slice.label}`,
                      summary: { metrics: [{ label: "Count", value: fmt(slice.value), color: theme.palette.primary.main }] },
                    },
                    { category: "W9", value: mapSliceToFilterValue("W9", slice.label) },
                  )
                }
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Full Linguist Directory */}
      <Box sx={{ px: 1 }}>
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Linguist Directory</Typography>
      </Box>
      <DashboardAllLinguists user={user} />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
        onRowClick={drillDown.onRowClick}
        loading={drillDown.loading}
      />
    </Box>
  );
};

export default DashboardV2AllLinguists;
